import { useTranslation } from 'react-i18next'
import { SocialCitationPost } from '@digital-twins/shared-types'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { Text } from '@/components/ui/Text'
import {
  CITATION_VERIFICATION,
  CitationVerification,
  citationVerificationCopy,
} from './citationVerification'
import { SocialCitationPreview } from '../KnowledgeHubPage/social/SocialCitationPostMeta'

/** Shared circle chip — inline markers and source-card index badges. */
export const CITATION_MARKER_CHIP_CLASS =
  'inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none'

interface ThreadCitationMarkerProps {
  index: number
  documentLabel: string
  pageNumber?: number | null
  pageUnknown?: boolean
  verification?: CitationVerification
  /** A cited social post previews as the post: thumbnail, author and excerpt. */
  socialPost?: SocialCitationPost | null
  excerpt?: string
  onOpen: (index: number) => void
}

export function ThreadCitationMarker({
  index,
  documentLabel,
  pageNumber,
  pageUnknown,
  verification = 'matched',
  socialPost,
  excerpt = '',
  onOpen,
}: ThreadCitationMarkerProps) {
  const { t } = useTranslation('app')

  const pageLabel =
    !pageUnknown && pageNumber != null ? ` · p.${pageNumber}` : ''
  const state = CITATION_VERIFICATION[verification]
  const copy = citationVerificationCopy(verification)
  const ariaLabel =
    verification === 'matched'
      ? t('threads.detail.sourceMarkerAria', {
          index,
          label: documentLabel,
        })
      : t('threads.detail.sourceMarkerAria', {
          index,
          label: `${documentLabel} — ${copy.label}`,
        })

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid={`thread-citation-marker-${index}`}
            data-verification={verification}
            aria-label={ariaLabel}
            className={cn(
              'relative -top-0.5 mx-0.5 align-super',
              CITATION_MARKER_CHIP_CLASS,
              state.markerClassName,
            )}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onOpen(index)
            }}
          >
            {index}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs space-y-1"
          data-testid={`thread-citation-tooltip-${index}`}
        >
          {socialPost ? (
            <SocialCitationPreview
              post={socialPost}
              excerpt={excerpt}
              testId={`thread-citation-social-preview-${index}`}
            />
          ) : (
            <p className="text-xs font-medium">
              {documentLabel}
              {pageLabel}
            </p>
          )}
          {verification === 'matched' ? null : (
            <Text size="xs" color="muted">
              {copy.description}
            </Text>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
